import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { Company, User } from '@core/models';
import { AuthService } from '@core/services';
import { take, zipWith } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * String template literal format for caching serialized state on a page.
 * The route must have a cache key name for the feature supporting caching.
 * The format is "${name}-${company.id}-${user.id} allowing for company and user specific state.
 * Users may have many roles across companies.
 * @example tasks-columns-1-33
 */
export type CacheKey = string & `${string}-${number}-${number}`;

/**
 * Cache keys require data from the company and user entities stored on the Java Web Token.
 * The data access is async via an observable, as a resolver it's a static string.
 * @param route
 * @param state
 * @see RouteCachingData
 * @see JWTModel
 */
export const cacheKeyResolver: ResolveFn<CacheKey> = (route, state) => {
  const authService = inject(AuthService);
  return inject(AuthService)
    .getCurrentCompany()
    .pipe(
      zipWith(authService.getCurrentUser()),
      map(([company, user]: [Company, User]) => {
        // make string in the cache key format from route and authenticated user information.
        let companyUserCacheKey: CacheKey = `${route.routeConfig.path}-${company.id}-${user.id}`;

        return companyUserCacheKey;
      }),
      // Due to challenges of getCurrentCompany returning new values. Take only once on load of the route.
      take(1)
    );
};
